<script setup lang="ts">
  import { useInfluencersStore } from '@/store/influencers';
  import { formatNumber } from '@/utils/strings';
  import { computed, onMounted, ref } from 'vue';
  import DeliverablePerformanceItem from './DeliverablePerformanceItem.vue';

  const influencerStore = useInfluencersStore();

  export interface ProfileMetric {
    icon: string;
    value: string | number;
    label: string;
    color?: string;
  }

  export interface Deliverable {
    image: string | null;
    influencer_id: number;
    followers: number;
    name: string;
    date: string;
    impressions: number;
    media_type: string;
    reach: number;
    saved: number;
    shares: number;
    submission_link: string;
    total_interactions: number;
    post_thumbnail: string;
    platform: string;
    cost: number;
    engagement_rate: number;
    er: string;
    cpm: number;
    cpe: number;
    cpr: number;
    occurrences: number;
  }

  interface Influencer extends Deliverable {
    deliverables: Deliverable[];
  }

  interface Props {
    influencer?: Influencer;
    isPublic?: boolean;
  }

  const props = defineProps<Props>();

  const metricsData = computed<ProfileMetric[]>(() => {
    if (!props.influencer) return [];

    return [
      {
        icon: 'lucide:eye',
        value: formatNumber(props.influencer.impressions, { notation: 'compact' }),
        label: 'Impressions',
        color: '#3B82F6',
      },
      {
        icon: 'lucide:users',
        value: formatNumber(props.influencer.reach, { notation: 'compact' }),
        label: 'Reach',
        color: '#22D3EE',
      },
      {
        icon: 'lucide:download',
        value: formatNumber(Math.abs(props.influencer.shares), { notation: 'compact' }),
        label: 'Shares',
        color: '#06B6D4',
      },
      {
        icon: 'lucide:bookmark',
        value: formatNumber(Math.abs(props.influencer.saved), { notation: 'compact' }),
        label: 'Saved',
        color: '#F59E0B',
      },
      {
        icon: 'lucide:trending-up',
        value: `${formatNumber(props.influencer.engagement_rate, { notation: 'compact', fractionDigits: 1 })}%`,
        label: 'Engagement',
        color: '#10B981',
      },
    ];
  });

  const username = computed(() => {
    if (!props.influencer) return '';

    try {
      const { platform, submission_link } = props.influencer;

      if (platform.toLowerCase() === 'twitter') {
        const match = submission_link.match(/x\.com\/([^/]+)/);
        return match ? match[1] : 'username';
      } else if (platform.toLowerCase() === 'instagram') {
        const match = submission_link.match(/instagram\.com\/([^/]+)/);

        return '';
      } else if (platform.toLowerCase() === 'tiktok') {
        const match = submission_link.match(/tiktok\.com\/@([^/]+)/);
        return match ? match[1] : 'tiktok';
      } else if (platform.toLowerCase() === 'facebook') {
        return 'facebook';
      }
    } catch (e) {
      return 'username';
    }
    return 'username';
  });

  const influencerAvatar = ref('');

  const showDeliverables = ref(false);

  const profileImage = computed(() => {
    if (!props.influencer) return '';
    return (
      props.influencer.image ||
      influencerAvatar.value ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(props.influencer.name)}&background=random`
    );
  });

  const getInfluencerData = async () => {
    if (!props.influencer || props.isPublic) return;
    const data = await influencerStore.doSelectInfluencersByIds([String(props.influencer.influencer_id)], true);
    influencerAvatar.value = data[0].avatar;
  };

  onMounted(async () => {
    await getInfluencerData();
  });
</script>

<template>
  <div v-if="!influencer" class="loading-state">
    <div class="loading-card">
      <div class="loading-spinner"></div>
      <p>Loading metrics...</p>
    </div>
  </div>

  <div v-else class="profile-card">
    <div class="profile-header">
      <img :src="profileImage" :alt="influencer.name" class="profile-avatar" />
      <div class="profile-info">
        <h2 class="profile-name">{{ influencer.name }}</h2>
        <p class="profile-username" v-if="username">@{{ username }}</p>
        <p class="profile-stats">
          {{ formatNumber(influencer.followers, { notation: 'compact' }) }} followers · {{ influencer.deliverables.length }} posts
        </p>
      </div>

      <button class="button deliverable-button flex items-center gap-8" @click="showDeliverables = !showDeliverables">
        {{ props.influencer?.deliverables?.length }} Deliverable{{ props.influencer?.deliverables?.length !== 1 ? 's' : '' }}
        <iconify-icon :icon="showDeliverables ? 'lucide:chevron-up' : 'lucide:chevron-down'" />
      </button>
    </div>

    <div class="metrics-container">
      <div v-for="(metric, index) in metricsData" :key="index" class="metric-card" :class="metric.label.toLowerCase()">
        <div class="metric-icon" :style="{ color: metric.color }">
          <iconify-icon :icon="metric.icon" />
        </div>
        <div class="metric-value">{{ metric.value }}</div>
        <div class="metric-label">{{ metric.label }}</div>
      </div>

      <div class="metric-card cpe-card">
        <div class="cpe-value">${{ influencer.cpe.toFixed(2) }}</div>
        <div class="metric-label">CPE</div>
      </div>
    </div>

    <Transition name="slide-fade">
      <div class="deliverables-wrapper flex flex-col gap-16" v-if="showDeliverables">
        <h3>Deliverables</h3>

        <div class="deliverables-container flex flex-col">
          <deliverable-performance-item
            v-for="(deliverable, index) in props.influencer?.deliverables"
            :key="`${deliverable.influencer_id}-${index}`"
            :deliverable="deliverable"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
  @import url('./style/creator-social-metrics.scss');
  @import '@/assets/styles/variables.scss';

  .deliverable-button {
    border: none;
    outline: none;
    cursor: pointer;
    font-weight: 500;
    color: $text-title;
  }

  .deliverables-wrapper {
    margin-top: 24px;
    background: $bg-lighter;
    padding: 24px;
    border-radius: 16px;
    border: 1px solid $input-border;
  }

  .deliverables-container {
    gap: 32px;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition:
      max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      margin-top 0.4s ease-in-out,
      padding 0.4s ease-in-out,
      transform 0.3s ease;
    overflow: hidden;
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    max-height: 0;
    margin-top: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    transform: translateY(-10px);
  }

  .slide-fade-enter-to,
  .slide-fade-leave-from {
    max-height: 1000px;
  }
</style>
